/**
 * Payout status shown beneath the schedule selector.
 *
 * Manual schedule → available balance and a "Pay out now" button.
 * Automatic schedule → the next expected payout date.
 *
 * The date is ESTIMATED and labelled as such. Stripe does not expose a "next payout date"
 * for automatic schedules — it only creates a payout object once one is initiated — so this
 * is derived from the interval and anchor. Stripe also skips weekends and bank holidays and
 * will not pay out an empty balance, so the real date can slip by a day or two.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import {
  addDays,
  addMonths,
  format,
  isBefore,
  nextDay,
  setDate,
  startOfDay,
} from "date-fns";

import { TwButton } from "../../components/tailwind";
import { useToast } from "../../provider/ToastProvider";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForEvent } from "../../utils/confighelpers";

const WEEKDAY_INDEX = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };

/** Best-effort next payout date from the schedule. Returns null when it can't be derived. */
export const estimateNextPayout = ({ interval, delayDays, weeklyAnchor, monthlyAnchor }) => {
  const today = startOfDay(new Date());

  if (interval === "daily") {
    return addDays(today, Number(delayDays) > 0 ? Number(delayDays) : 1);
  }

  if (interval === "weekly") {
    const dayIndex = WEEKDAY_INDEX[String(weeklyAnchor || "monday").toLowerCase()];
    return dayIndex === undefined ? null : nextDay(today, dayIndex);
  }

  if (interval === "monthly") {
    const anchor = Math.min(Math.max(parseInt(monthlyAnchor, 10) || 1, 1), 28);
    const thisMonth = setDate(today, anchor);
    return isBefore(thisMonth, today) ? setDate(addMonths(today, 1), anchor) : thisMonth;
  }

  return null;
};

const money = (amount, currency = "EUR") =>
  `${currency === "EUR" ? "€" : `${currency} `}${parseFloat(amount || 0).toFixed(2)}`;

export default function PayoutStatus({ interval, delayDays, weeklyAnchor, monthlyAnchor }) {
  const { t } = useTranslation();
  const { addErrorToast, addNotificationDialog } = useToast();

  const [balance, setBalance] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [payingOut, setPayingOut] = React.useState(false);

  const loadBalance = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance({
        method: "post",
        url: `${getUrlForEvent()}payments/getPayoutBalance`,
      });
      if (response?.data?.statusCode === 200) setBalance(response.data.data);
    } catch (e) {
      // Non-fatal: the schedule selector above still works without a balance.
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  const handlePayoutNow = async () => {
    if (payingOut) return;
    setPayingOut(true);
    try {
      const response = await axiosInstance({
        method: "post",
        url: `${getUrlForEvent()}payments/createManualPayout`,
      });
      if (response?.data?.statusCode === 200) {
        const payout = response.data.data;
        addNotificationDialog({
          dialogType: "success",
          dialogTitle: t("account.payoutStartedTitle", "Payout on its way"),
          dialogMessage: t(
            "account.payoutStartedMessage",
            "{{amount}} is being transferred to your bank account. It usually arrives within a few working days.",
            { amount: money(payout.amount, payout.currency) }
          ),
          dialogBtnTitle: t("inputFieldsTitle.ok", "OK"),
        });
        loadBalance();
      } else {
        addErrorToast(response?.data?.message || t("errors.somethingIsWrong"));
      }
    } catch (e) {
      addErrorToast(e?.response?.data?.message || t("errors.somethingIsWrong"));
    }
    setPayingOut(false);
  };

  if (loading) return null;

  const currency = balance?.currency || "EUR";
  const available = Number(balance?.availableAmount || 0);
  const pending = Number(balance?.pendingAmount || 0);

  if (interval === "manual") {
    return (
      <div className="rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">
              {t("account.availableForPayout", "Available for payout")}
            </p>
            <p className="text-2xl font-semibold mt-0.5">{money(available, currency)}</p>
            {pending > 0 ? (
              <p className="text-xs text-gray-500 mt-1">
                {t("account.pendingAmount", "{{amount}} still clearing", {
                  amount: money(pending, currency),
                })}
              </p>
            ) : null}
          </div>

          <TwButton
            variant="secondary"
            className="sm:min-w-32"
            disabled={payingOut || available <= 0 || balance?.payoutsEnabled === false}
            actionLoading={payingOut}
            onClick={handlePayoutNow}
          >
            {t("account.payoutNow", "Pay out now")}
          </TwButton>
        </div>

        {balance?.payoutsEnabled === false ? (
          <p className="text-xs text-amber-700 mt-3">
            {t("account.payoutsNotEnabled", "Payouts aren't enabled on your Stripe account yet.")}
          </p>
        ) : available <= 0 ? (
          <p className="text-xs text-gray-500 mt-3">
            {t(
              "account.noAvailableBalance",
              "Nothing to pay out yet. Recent sales stay pending until Stripe settles them."
            )}
          </p>
        ) : null}
      </div>
    );
  }

  const nextPayout = estimateNextPayout({ interval, delayDays, weeklyAnchor, monthlyAnchor });

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm text-gray-500">
        {t("account.nextPayout", "Next payout scheduled for")}
      </p>
      <p className="text-lg font-semibold mt-0.5">
        {nextPayout ? format(nextPayout, "EEEE d MMMM yyyy") : "—"}
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {t(
          "account.nextPayoutEstimate",
          "Estimated. Stripe skips weekends and bank holidays, and only pays out a balance that has cleared."
        )}
      </p>

      {available > 0 || pending > 0 ? (
        <p className="text-sm text-gray-600 mt-3">
          {t("account.balanceSummary", "{{available}} available · {{pending}} clearing", {
            available: money(available, currency),
            pending: money(pending, currency),
          })}
        </p>
      ) : null}
    </div>
  );
}
